<script setup lang="ts">
/**
 * Taste preview — Figma `Taste preview` (3448:113123). One row of a person's taste:
 * Style — photos 28×36, 4 apart; Color — circles 28 overlapping by 8 with a white ring;
 * Brand — logo circles 28 overlapping by 8. Four items, then "+N" on a solid fill.
 * Display only (T-134: previews are not clickable); the names are in the a11y tree.
 */
import { computed } from 'vue'
import {
  FAIR_TASTE_PREVIEW_MAX,
  fairTastePreviewSlice,
  type FairTastePreviewItem,
  type FairTastePreviewKind,
} from '@/ui/tokens/fair-taste-preview.data'

const props = withDefaults(
  defineProps<{
    kind: FairTastePreviewKind
    items: FairTastePreviewItem[]
    max?: number
    /** Accessible name of the row, e.g. "Styles". */
    label?: string
  }>(),
  { max: FAIR_TASTE_PREVIEW_MAX, label: undefined },
)

const slice = computed(() => fairTastePreviewSlice(props.items, props.max))
const names = computed(() => props.items.map((item) => item.name).join(', '))
</script>

<template>
  <div
    class="fair-taste-preview"
    :class="`fair-taste-preview--${kind}`"
    role="img"
    :aria-label="label ? `${label}: ${names}` : names"
    data-figma="Taste preview"
  >
    <span
      v-for="item in slice.shown"
      :key="item.id"
      class="fair-taste-preview__item"
      :title="item.name"
      :style="kind === 'color' && item.hex ? { background: item.hex } : undefined"
    >
      <img
        v-if="kind !== 'color' && item.image"
        class="fair-taste-preview__image"
        :src="item.image"
        alt=""
        loading="lazy"
        decoding="async"
      >
      <span
        v-else-if="kind === 'brand'"
        class="fair-taste-preview__initial"
        aria-hidden="true"
      >{{ item.name.charAt(0) }}</span>
    </span>
    <span
      v-if="slice.more"
      class="fair-taste-preview__item fair-taste-preview__more"
      aria-hidden="true"
    >+{{ slice.more }}</span>
  </div>
</template>
